import { get, parseInt } from "lodash";
import React from "react";
import { Form as FinalForm } from "react-final-form";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { Col, Row } from "reactstrap";
import AppBar from "src/components/AppBar/AppBar";
import { Container as FormContainer } from "src/components/Container/Container";
import { IconName } from "src/components/Icon/Icon";
import ManageProductItemsModal from "src/components/Modals/ManageProductItemsModal/ManageProductItemsModal";
import { IRootState } from "src/store/reducers";
import { fetchProduct } from "src/store/reducers/productsV2";
import CheckboxField from "../../components/Form/CheckboxFieldV2/CheckboxFieldV2";
import SingleSelectField from "../../components/Form/SingleSelectFieldV2/SingleSelectField";
import Toast from "../../components/Toast/Toast";
import * as S from "../../constants/StringConstants";
import {
  createProduct,
  deleteOption,
  fetchProductBuilderOptions,
  toggleProcessInProgressToast,
  toggleProcessStartFailedToast,
  toggleProcessStartSuccessToast,
  updateOption,
} from "../../store/reducers/productBuilder";
import "./ProductBuilder.scss";

export interface IProductBuilderProps extends StateProps, DispatchProps, RouteComponentProps<{ id: string }> {}

/**
 * [ optionKey, fieldName, fieldLabel]
 * optionKey, maps to redux dropdown options
 * fieldName, name for final submission
 */

const formFieldStructure = [
  ["occupationClassTypeList", "occClassTypeIDs", S.PB_OCCUPATION_CLASS_TYPE, "occ-classes"],
  ["renewabilityTypeList", "renewabilityTypeIDs", S.PB_RENEWABILITY_TYPE, "renewabilities"],
  ["bpTypeList", "bpTypeIDs", S.PB_BP_TYPE, "bps"],
  ["epTypeList", "epTypeIDs", S.PB_EP_TYPE, "eps"],
  ["ownOccTypeList", "ownOccTypeIDs", S.PB_OWN_OCC_TYPE, "own-occs"],
  ["residualTypeList", "residualTypeIDs", S.PB_RESIDUAL_TYPE, "residuals"],
  [
    "minimumResidualBenefitPayableTypeList",
    "minimumResidualBenefitPayableTypeIDs",
    S.PB_MINIMUM_RESIDUAL_BENEFIT_TYPE,
    "minimum-residual-benefit-payables",
  ],
  [
    "futureInsurabilityOptionsTypeList",
    "futureInsurabilityOptionsTypeIDs",
    S.PB_FUTURE_INSURABILITY_OPTIONS_TYPE,
    "future-insurability-options",
  ],
  ["airTypeList", "airTypeIDs", S.PB_AIR_TYPE, "airs"],
  ["colaTypeList", "colaTypeIDs", S.PB_COLA_TYPE, "colas"],
  [
    "mentalNervousLimitationTypeList",
    "mentalNervousLimitationTypeIDs",
    S.PB_MENTAL_NERVOUS_LIMITATION_TYPE,
    "mental-nervous-limitation",
  ],
  [
    "catastrophicBenefitRiderTypeList",
    "catastrophicBenefitRiderTypeIDs",
    S.PB_CATASTROPHIC_BENEFIT_TYPE,
    "catastrophic-benefit-riders",
  ],
  ["uniqueProvisionsTypeList", "uniqueProvisionsTypeIDs", S.PB_UNIQUE_PROVISIONS_TYPE, "unique-provisions"],
];

const addDefaultOption = (label, options) => [{ key: label, value: "", disabled: false }, ...options];

interface IProductBuilderState {
  modalOpen: boolean;
  selectedProductName?: string;
  selectEntityName?: string;
  selectedOptions: Array<{ key: string; value: number }>;
}

export class ProductBuilder extends React.Component<IProductBuilderProps, IProductBuilderState> {
  state: IProductBuilderState = {
    modalOpen: false,
    selectedProductName: "",
    selectEntityName: "",
    selectedOptions: [],
  };

  constructor(props) {
    super(props);
  }

  async componentDidMount() {
    await this.props.fetchProductBuilderOptions();
    const id = this.id;
    if (id) {
      await this.props.fetchProduct(id.toString());
    }
  }

  get id() {
    const id = this.props.match.params.id;
    return id != "new" ? id : null;
  }

  defaultProduct = undefined;
  get product() {
    if (this.id) {
      return this.props.products.map[this.id];
    } else {
      if (!this.defaultProduct) {
        this.defaultProduct = {};
      }

      return this.defaultProduct;
    }
  }

  onSubmit = async values => {
    // these need to be sent as arrays
    values["carrierTypeIDs"] = [values["carrierTypeID"]];
    values["designTypeIDs"] = [values["designTypeID"]];
    // ???
    values["productTypeIDs"] = values["productTypeID"];
    try {
      await this.props.createProduct(values);
      this.props.history.push("/products/all");
    } catch (e) {}
  };

  onValidate = values => {
    const errors = {};

    formFieldStructure.map(field => {
      const paramName = field[1];
      if (!values[paramName] || values[paramName].length == 0) {
        errors[paramName] = S.FORM_FIELD_REQUIRED;
      }
    });
    if (!values["productTypeID"]) {
      errors["productTypeID"] = S.FORM_FIELD_REQUIRED;
    }
    if (!values["carrierTypeID"]) {
      errors["carrierTypeID"] = S.FORM_FIELD_REQUIRED;
    }
    if (!values["designTypeID"]) {
      errors["designTypeID"] = S.FORM_FIELD_REQUIRED;
    }
    return errors;
  };

  getClassName(errors: any, name: string, dirtyFields: any) {
    var errorText = get(errors, name);
    var isDirtyField = get(dirtyFields, name + "[]");
    if (errorText && isDirtyField) {
      return "error";
    }

    return "";
  }

  productToFormValues = (product: any) => {
    return {
      ...product,
    };
  };

  handleSaveItems = async (updatedItems: Array<{ key: string; value?: number }>) => {
    const existingItems = [...this.state.selectedOptions];
    const diff = updatedItems.reduce(
      (acc, item, index) => {
        const oldIndex = existingItems.findIndex(o => o.value === item.value);

        if (oldIndex === -1) {
          // skip empty items
          if (item.key.length > 0) {
            acc.added.push(item);
          }
          return acc;
        }

        const old = existingItems.splice(oldIndex, 1)[0];
        if (old.key !== item.key) {
          acc.edited.push(item);
        }
        return acc;
      },
      { added: [], edited: [] },
    );

    const { selectEntityName } = this.state;
    const ops = [
      ...diff.added.map(item => updateOption(selectEntityName, item)),
      ...diff.edited.map(item => updateOption(selectEntityName, item)),
      ...existingItems.map(item => deleteOption(selectEntityName, item.value)),
    ];
    try {
      if (ops.length > 0) {
        await Promise.all(ops);
        await this.props.fetchProductBuilderOptions();
      }
    } catch (e) {
      alert(e);
    }
    this.setState({
      modalOpen: false,
    });
  };

  handleManageClick = (productName: string, optionsKey: string, entityName: string) => {
    const selectedOptions = [...(this.props.dropdownOptions[optionsKey] || [])];
    this.setState({
      modalOpen: true,
      selectEntityName: entityName,
      selectedProductName: productName,
      selectedOptions,
    });
  };

  handleCloseModal = () => {
    this.setState({ modalOpen: false });
  };

  render() {
    const { dropdownOptions, loading, createProcessFailed, products, saving } = this.props;
    const productLoading = products.loading;

    const productOptions = addDefaultOption(S.PB_DEFAULT_PRODUCT_TYPE, dropdownOptions.productTypeList);
    const carrierOptions = addDefaultOption(S.PB_DEFAULT_CARRIER_TYPE, dropdownOptions.carrierTypeList);
    const designOptions = addDefaultOption(S.PB_DEFAULT_DESIGN_TYPE, dropdownOptions.designTypeList);

    const defaultValues = {};
    const initialValues = this.product ? this.productToFormValues(this.product) : defaultValues;

    return (
      <>
        <FinalForm
          initialValues={initialValues}
          onSubmit={this.onSubmit}
          validate={this.onValidate}
          render={({ handleSubmit, submitting, invalid, errors, dirtyFields, form, values }) => {
            return (
              <>
                <AppBar icon={IconName.ProductBuilder} title={S.PROD_DETAIL_PAGE_TITLE} />

                <form
                  className="product-builder__wrap"
                  onSubmit={async event => {
                    await handleSubmit(event);
                    form.reset();
                  }}>
                  <FormContainer padding="3rem">
                    <h1 className="form-title mb-4">{S.PB_PRODUCT_INFORMATION}</h1>

                    <div className="product-builder__form-wrap">
                      <Row>
                        <Col sm="5">
                          <SingleSelectField
                            key={"product-type-field"}
                            name={"productTypeID"}
                            options={productOptions}
                            parse={value => parseInt(value)}
                            disabled={this.id}
                            label={S.PB_PRODUCT_TYPE}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm="5">
                          <SingleSelectField
                            key={"carrier-type-field"}
                            name={"carrierTypeID"}
                            options={carrierOptions}
                            parse={value => parseInt(value)}
                            disabled={this.id}
                            label={S.PB_CARRIER_TYPE}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm="5">
                          <SingleSelectField
                            key={"design-type-field"}
                            name={"designTypeID"}
                            options={designOptions}
                            parse={value => parseInt(value)}
                            disabled={this.id}
                            label={S.PB_DESIGN_TYPE}
                          />
                        </Col>
                      </Row>
                      {formFieldStructure.map(([optionsKey, name, label, entityName]) => {
                        const opts = dropdownOptions[optionsKey] || [];
                        const visibleCols = Math.min(4, Math.max(1, opts.length));
                        const offset = (visibleCols - 1) * 3;

                        return (
                          <Row key={name}>
                            <Col>
                              <Row className="mb-2">
                                <Col sm="12" className={this.getClassName(errors, name, dirtyFields)}>
                                  <h1 className="form-title">{label}</h1>
                                </Col>
                              </Row>

                              <Row className="mb-3">
                                <Col
                                  sm={{ size: 3, offset }}
                                  className="d-flex align-items-center justify-content-start">
                                  <button
                                    type="button"
                                    className="button-v2 button-v2__secondary small"
                                    onClick={() => this.handleManageClick(label, optionsKey, entityName)}>
                                    {S.PB_MANAGE_BUTTON}
                                  </button>
                                </Col>
                              </Row>

                              <Row>
                                {opts
                                  .sort((a, b) => a.key.localeCompare(b.key))
                                  .map(option => (
                                    <Col key={`${name}_${option.key}_${option.value}`} sm="3">
                                      <CheckboxField
                                        className="checkbox-field-v2 small-icon"
                                        label={option.key}
                                        name={`${name}[]`}
                                        value={option.value}
                                      />
                                    </Col>
                                  ))}
                              </Row>
                            </Col>
                          </Row>
                        );
                      })}
                    </div>
                    <div className="product-builder__spacer-40" />
                    <div className="product-builder__button-wrap">
                      <button className="button-v2 button-v2__primary" type="submit" disabled={submitting}>
                        {S.PB_SUBMIT_BUTTON}
                      </button>
                    </div>
                  </FormContainer>
                </form>
              </>
            );
          }}
        />

        {/* Modal Component for managing items*/}
        <ManageProductItemsModal
          open={this.state.modalOpen}
          onClose={this.handleCloseModal}
          productName={this.state.selectedProductName}
          items={this.state.selectedOptions}
          onSave={this.handleSaveItems}
        />
        <Toast
          message={S.PB_PRODUCTS_LOADED}
          onClose={() => {
            this.props.toggleProcessInProgressToast(false);
          }}
          open={loading || productLoading}
        />
        <Toast message={S.PB_PRODUCTS_SAVING} onClose={() => {}} open={saving} />
        <Toast
          message={S.PB_PRODUCT_CREATION_PROCESS_BUSY}
          onClose={() => {
            this.props.toggleProcessStartFailedToast(false);
          }}
          open={createProcessFailed}
        />
      </>
    );
  }
}

const mapStateToProps = ({
  productsV2: products,
  productBuilder: { loading, createProcessFailed, saving, ...dropdownOptions },
}: IRootState) => ({
  loading,
  saving,
  createProcessFailed,
  dropdownOptions,
  products,
});

const mapDispatchToProps = {
  fetchProductBuilderOptions,
  fetchProduct,
  createProduct,
  toggleProcessInProgressToast,
  toggleProcessStartSuccessToast,
  toggleProcessStartFailedToast,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(ProductBuilder);
